import React, { useEffect, useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    OutlinedInput,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Snackbar,
    Divider,
    FormControlLabel,
    Checkbox,
    Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { getUser } from '../../services/auth';
import { UserUtils } from '../../types';
import {
    getReviewDefinitions,
    getReviewTypes,
    getReviewFrequencies,
    getReviewEntityTypes,
    createReviewDefinition,
    updateReviewDefinition,
    deleteReviewDefinition,
    ReviewDefinition,
    ReviewType,
    ReviewFrequency,
    ReviewEntityType,
    CreateReviewDefinitionPayload,
    UpdateReviewDefinitionPayload,
    getReviewSchedules,
    getReviewScheduleByReviewDefinitionId,
    createReviewSchedule,
    updateReviewSchedule,
    deleteReviewSchedule,
    ReviewSchedule,
    CreateReviewSchedulePayload,
    UpdateReviewSchedulePayload,
    DailyScheduleConfig,
    WeeklyScheduleConfig,
    FortnightlyScheduleConfig,
    MonthlyScheduleConfig,
    QuarterlyScheduleConfig,
    YearlyScheduleConfig
} from '../../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface FormData {
    id?: number;
    name: string;
    display_label: string;
    description: string;
    review_type_id: number | '';
    review_frequency_id: number | '';
    submission_overdue_days: number | '';
    closure_overdue_days: number | '';
    applicable_review_entity_types: number[];
}

type ScheduleFrequency = 'minutely' | 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly';

interface ScheduleFormData {
    id?: number;
    enabled: boolean;
    frequency: ScheduleFrequency;
    // Daily config
    daily_hour: number;
    daily_minute: number;
    // Weekly config
    weekly_day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
    weekly_hour: number;
    weekly_minute: number;
    // Fortnightly config
    fortnightly_day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
    fortnightly_hour: number;
    fortnightly_minute: number;
    // Monthly config
    monthly_day: number;
    monthly_hour: number;
    monthly_minute: number;
    // Quarterly config
    quarterly_month: 1 | 2 | 3;
    quarterly_day: number;
    quarterly_hour: number;
    quarterly_minute: number;
    // Yearly config
    yearly_month: number;
    yearly_day: number;
    yearly_hour: number;
    yearly_minute: number;
}

// Display order for review frequencies by periodicity (shortest to longest).
// DB ids are not in periodic order, so we sort by name for the dropdown.
const FREQUENCY_PERIODICITY_ORDER: { [key: string]: number } = {
    MINUTELY: -1,
    DAILY: 0,
    WEEKLY: 1,
    FORTNIGHTLY: 2,
    MONTHLY: 3,
    QUARTERLY: 4,
    YEARLY: 5,
    ANNUAL: 5,
};

const sortFrequenciesByPeriodicity = (frequencies: ReviewFrequency[]): ReviewFrequency[] => {
    const orderOf = (name: string) => {
        const order = FREQUENCY_PERIODICITY_ORDER[name?.toUpperCase()];
        return order === undefined ? Number.MAX_SAFE_INTEGER : order;
    };
    return [...frequencies].sort((a, b) => orderOf(a.name) - orderOf(b.name));
};

const ReviewDefinitions = () => {
    const navigate = useNavigate();
    const [reviewDefinitions, setReviewDefinitions] = useState<ReviewDefinition[]>([]);
    const [reviewTypes, setReviewTypes] = useState<ReviewType[]>([]);
    const [reviewFrequencies, setReviewFrequencies] = useState<ReviewFrequency[]>([]);
    const [reviewEntityTypes, setReviewEntityTypes] = useState<ReviewEntityType[]>([]);
    const [reviewSchedules, setReviewSchedules] = useState<ReviewSchedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [definitionToDelete, setDefinitionToDelete] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
    
    const currentUser = getUser();
    const isAdmin = currentUser ? UserUtils.isAdmin(currentUser) : false;
    
    const [formData, setFormData] = useState<FormData>({
        name: '',
        display_label: '',
        description: '',
        review_type_id: '',
        review_frequency_id: '',
        submission_overdue_days: '',
        closure_overdue_days: '',
        applicable_review_entity_types: []
    });

    const [scheduleFormData, setScheduleFormData] = useState<ScheduleFormData>({
        enabled: false,
        frequency: 'weekly',
        daily_hour: 9,
        daily_minute: 0,
        weekly_day: 'monday',
        weekly_hour: 9,
        weekly_minute: 0,
        fortnightly_day: 'monday',
        fortnightly_hour: 9,
        fortnightly_minute: 0,
        monthly_day: 1,
        monthly_hour: 9,
        monthly_minute: 0,
        quarterly_month: 1,
        quarterly_day: 1,
        quarterly_hour: 9,
        quarterly_minute: 0,
        yearly_month: 1,
        yearly_day: 1,
        yearly_hour: 9,
        yearly_minute: 0
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [defsRes, typesRes, freqsRes, entityTypesRes, schedulesRes] = await Promise.all([
                getReviewDefinitions(),
                getReviewTypes(),
                getReviewFrequencies(),
                getReviewEntityTypes(),
                getReviewSchedules()
            ]);
            
            setReviewDefinitions(defsRes.data);
            setReviewTypes(typesRes.data);
            setReviewFrequencies(freqsRes.data);
            setReviewEntityTypes(entityTypesRes.data);
            setReviewSchedules(schedulesRes.data);
            setError('');
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = async (mode: 'create' | 'edit', definition?: ReviewDefinition) => {
        setDialogMode(mode);
        if (mode === 'edit' && definition) {
            setFormData({
                id: definition.id,
                name: definition.name,
                display_label: definition.display_label,
                description: definition.description,
                review_type_id: definition.review_type_id,
                review_frequency_id: definition.review_frequency_id ?? '',
                submission_overdue_days: definition.submission_overdue_days,
                closure_overdue_days: definition.closure_overdue_days,
                applicable_review_entity_types: definition.applicable_review_entity_type_ids
            });
            
            // Map review frequency to schedule frequency
            const scheduleFreq = mapReviewFrequencyToScheduleFrequency(definition.review_frequency_id);
            
            // Check if this is a PERIODIC review type
            const isPeriodic = isPeriodicReviewType(definition.review_type_id);
            
            // Fetch existing schedule using the new API
            try {
                const scheduleResponse = await getReviewScheduleByReviewDefinitionId(definition.id);
                const existingSchedule = scheduleResponse.data;
                
                if (existingSchedule && existingSchedule.id && existingSchedule.schedule_config) {
                    // Use the parsed schedule_config from backend
                    const config = existingSchedule.schedule_config;
                    
                    // Extract and cast values to proper types
                    const weeklyDay = (config.day_of_week || 'monday') as 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
                    const quarterlyMonth = (config.month_in_quarter || 1) as 1 | 2 | 3;
                    
                    setScheduleFormData({
                        id: existingSchedule.id,
                        enabled: true,
                        frequency: scheduleFreq,
                        daily_hour: config.hour || 9,
                        daily_minute: config.minute || 0,
                        weekly_day: weeklyDay,
                        weekly_hour: config.hour || 9,
                        weekly_minute: config.minute || 0,
                        fortnightly_day: weeklyDay,
                        fortnightly_hour: config.hour || 9,
                        fortnightly_minute: config.minute || 0,
                        monthly_day: config.day_of_month || 1,
                        monthly_hour: config.hour || 9,
                        monthly_minute: config.minute || 0,
                        quarterly_month: quarterlyMonth,
                        quarterly_day: config.day_of_month || 1,
                        quarterly_hour: config.hour || 9,
                        quarterly_minute: config.minute || 0,
                        yearly_month: config.month || 1,
                        yearly_day: config.day_of_month || 1,
                        yearly_hour: config.hour || 9,
                        yearly_minute: config.minute || 0
                    });
                } else {
                    resetScheduleForm();
                    // For PERIODIC review types, enable the schedule section by default
                    setScheduleFormData(prev => ({ 
                        ...prev, 
                        frequency: scheduleFreq,
                        enabled: isPeriodic
                    }));
                }
            } catch (error) {
                // No schedule exists, reset form
                resetScheduleForm();
                // For PERIODIC review types, enable the schedule section by default
                setScheduleFormData(prev => ({ 
                    ...prev, 
                    frequency: scheduleFreq,
                    enabled: isPeriodic
                }));
            }
        } else {
            setFormData({
                name: '',
                display_label: '',
                description: '',
                review_type_id: '',
                review_frequency_id: '',
                submission_overdue_days: '',
                closure_overdue_days: '',
                applicable_review_entity_types: []
            });
            resetScheduleForm();
        }
        setOpenDialog(true);
    };

    const resetScheduleForm = () => {
        setScheduleFormData({
            enabled: false,
            frequency: 'weekly',
            daily_hour: 9,
            daily_minute: 0,
            weekly_day: 'monday',
            weekly_hour: 9,
            weekly_minute: 0,
            fortnightly_day: 'monday',
            fortnightly_hour: 9,
            fortnightly_minute: 0,
            monthly_day: 1,
            monthly_hour: 9,
            monthly_minute: 0,
            quarterly_month: 1,
            quarterly_day: 1,
            quarterly_hour: 9,
            quarterly_minute: 0,
            yearly_month: 1,
            yearly_day: 1,
            yearly_hour: 9,
            yearly_minute: 0
        });
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData({
            name: '',
            display_label: '',
            description: '',
            review_type_id: '',
            review_frequency_id: '',
            submission_overdue_days: '',
            closure_overdue_days: '',
            applicable_review_entity_types: []
        });
        resetScheduleForm();
    };

    const getMaxOverdueDaysByFrequency = (reviewFrequencyId: number | ''): number | null => {
        if (!reviewFrequencyId) return null;
        const reviewFrequency = reviewFrequencies.find(freq => freq.id === reviewFrequencyId);
        if (!reviewFrequency) return null;
        if (reviewFrequency.name === 'MINUTELY') return 1;
        if (reviewFrequency.name === 'DAILY') return 1;
        if (reviewFrequency.name === 'WEEKLY') return 7;
        if (reviewFrequency.name === 'FORTNIGHTLY') return 14;
        if (reviewFrequency.name === 'MONTHLY') return 30;
        if (reviewFrequency.name === 'QUARTERLY') return 90;
        if (reviewFrequency.name === 'YEARLY' || reviewFrequency.name === 'ANNUAL') return 365;
        return null;
    };

    const handleSubmit = async () => {
        try {
            // Check if review_frequency_id is required (only for PERIODIC review types)
            const isPeriodic = isPeriodicReviewType(formData.review_type_id);
            const isFrequencyRequired = isPeriodic && !formData.review_frequency_id;
            
            if (!formData.name || !formData.display_label || !formData.description || 
                !formData.review_type_id || isFrequencyRequired || 
                formData.applicable_review_entity_types.length === 0) {
                setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
                return;
            }

            if (
                formData.submission_overdue_days === '' ||
                formData.closure_overdue_days === '' ||
                formData.submission_overdue_days <= 0 ||
                formData.closure_overdue_days <= 0
            ) {
                setSnackbar({
                    open: true,
                    message: 'Submission overdue days and closure overdue days must be positive integers',
                    severity: 'error'
                });
                return;
            }

            if (!Number.isInteger(formData.submission_overdue_days) || !Number.isInteger(formData.closure_overdue_days)) {
                setSnackbar({
                    open: true,
                    message: 'Submission overdue days and closure overdue days must be integers',
                    severity: 'error'
                });
                return;
            }

            if (isPeriodic) {
                const maxOverdueDays = getMaxOverdueDaysByFrequency(formData.review_frequency_id);
                if (
                    maxOverdueDays &&
                    (formData.submission_overdue_days > maxOverdueDays ||
                        formData.closure_overdue_days > maxOverdueDays)
                ) {
                    setSnackbar({
                        open: true,
                        message: `For selected frequency, overdue days must be <= ${maxOverdueDays}`,
                        severity: 'error'
                    });
                    return;
                }
            }

            let reviewDefinitionId: number;

            if (dialogMode === 'create') {
                const payload: CreateReviewDefinitionPayload = {
                    name: formData.name,
                    display_label: formData.display_label,
                    description: formData.description,
                    review_type_id: formData.review_type_id as number,
                    submission_overdue_days: formData.submission_overdue_days as number,
                    closure_overdue_days: formData.closure_overdue_days as number,
                    applicable_review_entity_types: formData.applicable_review_entity_types
                };
                // Only include review_frequency_id for PERIODIC review types
                if (isPeriodic && formData.review_frequency_id) {
                    payload.review_frequency_id = formData.review_frequency_id as number;
                }
                const response = await createReviewDefinition(payload);
                reviewDefinitionId = typeof response.data === 'number' ? response.data : response.data.id;
                setSnackbar({ open: true, message: 'Review definition created successfully', severity: 'success' });
            } else {
                const payload: UpdateReviewDefinitionPayload = {
                    id: formData.id!,
                    name: formData.name,
                    display_label: formData.display_label,
                    description: formData.description,
                    review_type_id: formData.review_type_id as number,
                    submission_overdue_days: formData.submission_overdue_days as number,
                    closure_overdue_days: formData.closure_overdue_days as number,
                    applicable_review_entity_types: formData.applicable_review_entity_types
                };
                // Only include review_frequency_id for PERIODIC review types
                if (isPeriodic && formData.review_frequency_id) {
                    payload.review_frequency_id = formData.review_frequency_id as number;
                }
                await updateReviewDefinition(payload);
                reviewDefinitionId = formData.id!;
                setSnackbar({ open: true, message: 'Review definition updated successfully', severity: 'success' });
            }

            // Handle schedule creation/update/deletion
            if (scheduleFormData.enabled) {
                await handleScheduleSave(reviewDefinitionId);
            } else if (scheduleFormData.id) {
                // If schedule was disabled and exists, delete it
                try {
                    await deleteReviewSchedule(scheduleFormData.id);
                } catch (err) {
                    console.error('Error deleting schedule:', err);
                }
            }
            
            handleCloseDialog();
            fetchData();
        } catch (err) {
            console.error('Error saving review definition:', err);
            setSnackbar({ open: true, message: 'Failed to save review definition', severity: 'error' });
        }
    };

    const handleScheduleSave = async (reviewDefinitionId: number) => {
        try {
            // Derive schedule frequency from review frequency
            const scheduleFrequency = mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id);
            
            // Check if a schedule already exists for this review definition
            const existingSchedule = reviewSchedules.find(s => s.review_definition_id === reviewDefinitionId);
            const scheduleId = scheduleFormData.id || existingSchedule?.id;
            
            // Build schedule config based on frequency
            let schedulePayload: CreateReviewSchedulePayload | UpdateReviewSchedulePayload;
            
            const basePayload = {
                review_definition_id: reviewDefinitionId,
                frequency: scheduleFrequency
            };

            if (scheduleFrequency === 'minutely') {
                schedulePayload = { ...basePayload };
            } else if (scheduleFrequency === 'daily') {
                const daily_config: DailyScheduleConfig = {
                    hour: scheduleFormData.daily_hour,
                    minute: scheduleFormData.daily_minute
                };
                schedulePayload = { ...basePayload, daily_config };
            } else if (scheduleFrequency === 'weekly') {
                const weekly_config: WeeklyScheduleConfig = {
                    day_of_week: scheduleFormData.weekly_day,
                    hour: scheduleFormData.weekly_hour,
                    minute: scheduleFormData.weekly_minute
                };
                schedulePayload = { ...basePayload, weekly_config };
            } else if (scheduleFrequency === 'fortnightly') {
                const fortnightly_config: FortnightlyScheduleConfig = {
                    day_of_week: scheduleFormData.fortnightly_day,
                    hour: scheduleFormData.fortnightly_hour,
                    minute: scheduleFormData.fortnightly_minute
                };
                schedulePayload = { ...basePayload, fortnightly_config };
            } else if (scheduleFrequency === 'monthly') {
                const monthly_config: MonthlyScheduleConfig = {
                    day_of_month: scheduleFormData.monthly_day,
                    hour: scheduleFormData.monthly_hour,
                    minute: scheduleFormData.monthly_minute
                };
                schedulePayload = { ...basePayload, monthly_config };
            } else if (scheduleFrequency === 'quarterly') {
                const quarterly_config: QuarterlyScheduleConfig = {
                    month_in_quarter: scheduleFormData.quarterly_month,
                    day_of_month: scheduleFormData.quarterly_day,
                    hour: scheduleFormData.quarterly_hour,
                    minute: scheduleFormData.quarterly_minute
                };
                schedulePayload = { ...basePayload, quarterly_config };
            } else {
                const yearly_config: YearlyScheduleConfig = {
                    month: scheduleFormData.yearly_month,
                    day_of_month: scheduleFormData.yearly_day,
                    hour: scheduleFormData.yearly_hour,
                    minute: scheduleFormData.yearly_minute
                };
                schedulePayload = { ...basePayload, yearly_config };
            }

            if (scheduleId) {
                // Update existing schedule
                await updateReviewSchedule({ ...schedulePayload, id: scheduleId } as UpdateReviewSchedulePayload);
            } else {
                // Create new schedule
                await createReviewSchedule(schedulePayload);
            }
        } catch (err) {
            console.error('Error saving schedule:', err);
            throw err;
        }
    };

    const handleDeleteClick = (id: number) => {
        setDefinitionToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (definitionToDelete === null) return;

        try {
            await deleteReviewDefinition(definitionToDelete);
            setSnackbar({ open: true, message: 'Review definition deleted successfully', severity: 'success' });
            fetchData();
        } catch (err) {
            console.error('Error deleting review definition:', err);
            setSnackbar({ open: true, message: 'Failed to delete review definition', severity: 'error' });
        } finally {
            setDeleteConfirmOpen(false);
            setDefinitionToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setDefinitionToDelete(null);
    };

    const getReviewTypeName = (id: number) => {
        const type = reviewTypes.find(t => t.id === id);
        return type ? type.display_label : '';
    };

    const getReviewFrequencyName = (id: number | '') => {
        if (!id) return '';
        const freq = reviewFrequencies.find(f => f.id === id);
        return freq ? freq.display_label : '';
    };

    const getEntityTypeNames = (ids: number[]) => {
        return ids.map(id => {
            const entityType = reviewEntityTypes.find(et => et.id === id);
            return entityType ? entityType.display_label : '';
        }).filter(name => name);
    };

    const isPeriodicReviewType = (reviewTypeId: number | '') => {
        if (!reviewTypeId) return false;
        const reviewType = reviewTypes.find(rt => rt.id === reviewTypeId);
        return reviewType?.name === 'PERIODIC';
    };

    const isAdhocReviewType = (reviewTypeId: number | '') => {
        if (!reviewTypeId) return false;
        const reviewType = reviewTypes.find(rt => rt.id === reviewTypeId);
        return reviewType?.name === 'ADHOC';
    };

    const isGatedReviewType = (reviewTypeId: number | '') => {
        if (!reviewTypeId) return false;
        const reviewType = reviewTypes.find(rt => rt.id === reviewTypeId);
        return reviewType?.name === 'GATED';
    };

    const isAdhocOrGatedReviewType = (reviewTypeId: number | '') => {
        return isAdhocReviewType(reviewTypeId) || isGatedReviewType(reviewTypeId);
    };

    const mapReviewFrequencyToScheduleFrequency = (reviewFrequencyId: number | ''): ScheduleFrequency => {
        if (!reviewFrequencyId) return 'weekly';
        const frequency = reviewFrequencies.find(f => f.id === reviewFrequencyId);
        if (!frequency) return 'weekly';
        
        const name = frequency.name.toLowerCase();
        if (name === 'minutely') return 'minutely';
        if (name === 'daily') return 'daily';
        if (name === 'weekly') return 'weekly';
        if (name === 'fortnightly') return 'fortnightly';
        if (name === 'monthly') return 'monthly';
        if (name === 'quarterly') return 'quarterly';
        if (name === 'annual' || name === 'yearly') return 'yearly';
        return 'weekly';
    };

    const parseDayOfWeek = (cronDay: string): 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday' => {
        // Cron day of week: 0=Sunday, 1=Monday, ..., 6=Saturday
        const dayMap: { [key: string]: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday' } = {
            '0': 'sunday',
            '1': 'monday',
            '2': 'tuesday',
            '3': 'wednesday',
            '4': 'thursday',
            '5': 'friday',
            '6': 'saturday'
        };
        return dayMap[cronDay] || 'monday';
    };

    const parseQuarterlyMonth = (cronMonth: string): 1 | 2 | 3 => {
        // Quarterly cron: "2,5,8,11" means months 2,5,8,11 (Feb, May, Aug, Nov)
        // Extract the first month and determine which month in quarter it is
        if (cronMonth.includes(',')) {
            const months = cronMonth.split(',').map(m => parseInt(m));
            const firstMonth = months[0];
            // Determine position in quarter: 2=1st, 5=2nd, 8=3rd, 11=1st of next quarter
            if ([2, 5, 8, 11].includes(firstMonth)) {
                return ((firstMonth - 2) % 3 + 1) as 1 | 2 | 3;
            }
        }
        return 1;
    };

    return (
        <>
            <TitleBar title="" />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            aria-label="Back"
                            onClick={() => navigate('/home')}
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                p: 1,
                                backgroundColor: '#f5f5f5',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                                mr: 2,
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h4" fontWeight="bold">
                            Review Definitions
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('create')}
                    >
                        Add Review Definition
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Typography>Loading...</Typography>
                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 1200 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }}><strong>Name</strong></TableCell>
                                    <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}><strong>Display Label</strong></TableCell>
                                    <TableCell sx={{ width: '25%' }}><strong>Description</strong></TableCell>
                                    <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }}><strong>Review Type</strong></TableCell>
                                    <TableCell sx={{ width: '10%', whiteSpace: 'nowrap' }}><strong>Frequency</strong></TableCell>
                                    <TableCell sx={{ width: '18%', whiteSpace: 'nowrap' }}><strong>Applicable Entity Types</strong></TableCell>
                                    {isAdmin && (
                                        <TableCell align="center" sx={{ width: '8%', whiteSpace: 'nowrap' }}><strong>Actions</strong></TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reviewDefinitions.map((definition) => (
                                    <TableRow key={definition.id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{definition.name}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{definition.display_label}</TableCell>
                                        <TableCell>{definition.description}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{getReviewTypeName(definition.review_type_id)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{getReviewFrequencyName(definition.review_frequency_id)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {getEntityTypeNames(definition.applicable_review_entity_type_ids).map((name, idx) => (
                                                    <Chip key={idx} label={name} size="small" />
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            {isAdmin && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenDialog('edit', definition)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(definition.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {dialogMode === 'create' ? 'Create Review Definition' : 'Edit Review Definition'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <TextField
                                label="Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Display Label"
                                value={formData.display_label}
                                onChange={(e) => setFormData({ ...formData, display_label: e.target.value })}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                required
                                fullWidth
                                multiline
                                rows={3}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Review Type</InputLabel>
                                        <Select
                                            value={formData.review_type_id}
                                            onChange={(e) => setFormData({ ...formData, review_type_id: e.target.value as number })}
                                            label="Review Type"
                                        >
                                            {reviewTypes.map((type) => (
                                                <MenuItem key={type.id} value={type.id}>
                                                    {type.display_label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth required disabled={!isPeriodicReviewType(formData.review_type_id)}>
                                        <InputLabel>Review Frequency</InputLabel>
                                        <Select
                                            value={formData.review_frequency_id}
                                            onChange={(e) => {
                                                const newFrequencyId = e.target.value as number;
                                                setFormData({ ...formData, review_frequency_id: newFrequencyId });
                                                const scheduleFreq = mapReviewFrequencyToScheduleFrequency(newFrequencyId);
                                                setScheduleFormData({ ...scheduleFormData, frequency: scheduleFreq });
                                            }}
                                            label="Review Frequency"
                                            disabled={!isPeriodicReviewType(formData.review_type_id)}
                                        >
                                            {sortFrequenciesByPeriodicity(reviewFrequencies).map((freq) => (
                                                <MenuItem key={freq.id} value={freq.id}>
                                                    {freq.display_label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        label="Submission Overdue Days"
                                        type="number"
                                        value={formData.submission_overdue_days}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setFormData({
                                                ...formData,
                                                submission_overdue_days: value === '' ? '' : parseInt(value, 10)
                                            });
                                        }}
                                        required
                                        fullWidth
                                        inputProps={{ min: 1, step: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        label="Closure Overdue Days"
                                        type="number"
                                        value={formData.closure_overdue_days}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setFormData({
                                                ...formData,
                                                closure_overdue_days: value === '' ? '' : parseInt(value, 10)
                                            });
                                        }}
                                        required
                                        fullWidth
                                        inputProps={{ min: 1, step: 1 }}
                                    />
                                </Grid>
                            </Grid>
                            <FormControl fullWidth required>
                                <InputLabel>Applicable Entity Types</InputLabel>
                                <Select
                                    multiple
                                    value={formData.applicable_review_entity_types}
                                    onChange={(e) => setFormData({ 
                                        ...formData, 
                                        applicable_review_entity_types: e.target.value as number[] 
                                    })}
                                    input={<OutlinedInput label="Applicable Entity Types" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as number[]).map((value) => {
                                                const entityType = reviewEntityTypes.find(et => et.id === value);
                                                return (
                                                    <Chip key={value} label={entityType?.display_label || ''} size="small" />
                                                );
                                            })}
                                        </Box>
                                    )}
                                >
                                    {reviewEntityTypes.map((entityType) => (
                                        <MenuItem key={entityType.id} value={entityType.id}>
                                            {entityType.display_label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Schedule Configuration Section - Only for PERIODIC review types */}
                            {isPeriodicReviewType(formData.review_type_id) && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={scheduleFormData.enabled}
                                                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, enabled: e.target.checked })}
                                                />
                                            }
                                            label={<Typography variant="h6">Enable Automated Schedule</Typography>}
                                        />
                                
                                {scheduleFormData.enabled && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    Schedule configuration for <strong>{getReviewFrequencyName(formData.review_frequency_id)}</strong> reviews
                                                </Typography>
                                            </Grid>

                                            {/* Minutely Configuration (scheduler test) */}
                                            {mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id) === 'minutely' && (
                                                <Grid item xs={12}>
                                                    <Alert severity="warning">
                                                        This schedule fires <strong>every minute</strong> (cron <code>* * * * *</code>).
                                                        Use it only to verify that APScheduler jobs are triggering, then disable it.
                                                    </Alert>
                                                </Grid>
                                            )}

                                            {/* Daily Configuration */}
                                            {mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id) === 'daily' && (
                                                <>
                                                    <Grid item xs={6} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Hour (0-23)"
                                                            value={scheduleFormData.daily_hour}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                daily_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 23 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Minute (0-59)"
                                                            value={scheduleFormData.daily_minute}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                daily_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 59 }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}

                                            {/* Weekly Configuration */}
                                            {mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id) === 'weekly' && (
                                                <>
                                                    <Grid item xs={12} sm={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Day of Week</InputLabel>
                                                            <Select
                                                                value={scheduleFormData.weekly_day}
                                                                onChange={(e) => setScheduleFormData({ 
                                                                    ...scheduleFormData, 
                                                                    weekly_day: e.target.value as any
                                                                })}
                                                                label="Day of Week"
                                                            >
                                                                <MenuItem value="monday">Monday</MenuItem>
                                                                <MenuItem value="tuesday">Tuesday</MenuItem>
                                                                <MenuItem value="wednesday">Wednesday</MenuItem>
                                                                <MenuItem value="thursday">Thursday</MenuItem>
                                                                <MenuItem value="friday">Friday</MenuItem>
                                                                <MenuItem value="saturday">Saturday</MenuItem>
                                                                <MenuItem value="sunday">Sunday</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={6} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Hour (0-23)"
                                                            value={scheduleFormData.weekly_hour}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                weekly_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 23 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Minute (0-59)"
                                                            value={scheduleFormData.weekly_minute}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                weekly_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 59 }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}

                                            {/* Fortnightly Configuration */}
                                            {mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id) === 'fortnightly' && (
                                                <>
                                                    <Grid item xs={12}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Fortnightly reviews run on the selected day every other week (alternating ISO weeks).
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Day of Week</InputLabel>
                                                            <Select
                                                                value={scheduleFormData.fortnightly_day}
                                                                onChange={(e) => setScheduleFormData({ 
                                                                    ...scheduleFormData, 
                                                                    fortnightly_day: e.target.value as any
                                                                })}
                                                                label="Day of Week"
                                                            >
                                                                <MenuItem value="monday">Monday</MenuItem>
                                                                <MenuItem value="tuesday">Tuesday</MenuItem>
                                                                <MenuItem value="wednesday">Wednesday</MenuItem>
                                                                <MenuItem value="thursday">Thursday</MenuItem>
                                                                <MenuItem value="friday">Friday</MenuItem>
                                                                <MenuItem value="saturday">Saturday</MenuItem>
                                                                <MenuItem value="sunday">Sunday</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={6} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Hour (0-23)"
                                                            value={scheduleFormData.fortnightly_hour}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                fortnightly_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 23 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Minute (0-59)"
                                                            value={scheduleFormData.fortnightly_minute}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                fortnightly_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 59 }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}

                                            {/* Monthly Configuration */}
                                            {mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id) === 'monthly' && (
                                                <>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Day of Month (1-28)"
                                                            value={scheduleFormData.monthly_day}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                monthly_day: Math.max(1, Math.min(28, parseInt(e.target.value) || 1))
                                                            })}
                                                            inputProps={{ min: 1, max: 28 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Hour (0-23)"
                                                            value={scheduleFormData.monthly_hour}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                monthly_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 23 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Minute (0-59)"
                                                            value={scheduleFormData.monthly_minute}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                monthly_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 59 }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}

                                            {/* Quarterly Configuration */}
                                            {mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id) === 'quarterly' && (
                                                <>
                                                    <Grid item xs={12} sm={3}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Month in Quarter</InputLabel>
                                                            <Select
                                                                value={scheduleFormData.quarterly_month}
                                                                onChange={(e) => setScheduleFormData({ 
                                                                    ...scheduleFormData, 
                                                                    quarterly_month: e.target.value as 1 | 2 | 3
                                                                })}
                                                                label="Month in Quarter"
                                                            >
                                                                <MenuItem value={1}>1st Month (Jan/Apr/Jul/Oct)</MenuItem>
                                                                <MenuItem value={2}>2nd Month (Feb/May/Aug/Nov)</MenuItem>
                                                                <MenuItem value={3}>3rd Month (Mar/Jun/Sep/Dec)</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Day (1-28)"
                                                            value={scheduleFormData.quarterly_day}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                quarterly_day: Math.max(1, Math.min(28, parseInt(e.target.value) || 1))
                                                            })}
                                                            inputProps={{ min: 1, max: 28 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Hour (0-23)"
                                                            value={scheduleFormData.quarterly_hour}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                quarterly_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 23 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Minute (0-59)"
                                                            value={scheduleFormData.quarterly_minute}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                quarterly_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 59 }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}

                                            {/* Yearly Configuration */}
                                            {mapReviewFrequencyToScheduleFrequency(formData.review_frequency_id) === 'yearly' && (
                                                <>
                                                    <Grid item xs={12} sm={3}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Month</InputLabel>
                                                            <Select
                                                                value={scheduleFormData.yearly_month}
                                                                onChange={(e) => setScheduleFormData({ 
                                                                    ...scheduleFormData, 
                                                                    yearly_month: e.target.value as number
                                                                })}
                                                                label="Month"
                                                            >
                                                                <MenuItem value={1}>January</MenuItem>
                                                                <MenuItem value={2}>February</MenuItem>
                                                                <MenuItem value={3}>March</MenuItem>
                                                                <MenuItem value={4}>April</MenuItem>
                                                                <MenuItem value={5}>May</MenuItem>
                                                                <MenuItem value={6}>June</MenuItem>
                                                                <MenuItem value={7}>July</MenuItem>
                                                                <MenuItem value={8}>August</MenuItem>
                                                                <MenuItem value={9}>September</MenuItem>
                                                                <MenuItem value={10}>October</MenuItem>
                                                                <MenuItem value={11}>November</MenuItem>
                                                                <MenuItem value={12}>December</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Day (1-28)"
                                                            value={scheduleFormData.yearly_day}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                yearly_day: Math.max(1, Math.min(28, parseInt(e.target.value) || 1))
                                                            })}
                                                            inputProps={{ min: 1, max: 28 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Hour (0-23)"
                                                            value={scheduleFormData.yearly_hour}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                yearly_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 23 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Minute (0-59)"
                                                            value={scheduleFormData.yearly_minute}
                                                            onChange={(e) => setScheduleFormData({ 
                                                                ...scheduleFormData, 
                                                                yearly_minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0))
                                                            })}
                                                            inputProps={{ min: 0, max: 59 }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </Box>
                                )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button onClick={handleSubmit} variant="contained">
                            {dialogMode === 'create' ? 'Create' : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={deleteConfirmOpen}
                    onClose={handleDeleteCancel}
                    aria-labelledby="delete-dialog-title"
                    aria-describedby="delete-dialog-description"
                >
                    <DialogTitle id="delete-dialog-title">
                        Confirm Delete
                    </DialogTitle>
                    <DialogContent>
                        <Typography id="delete-dialog-description">
                            Are you sure you want to delete this review definition? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default ReviewDefinitions;
